import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useRevealRef, type Lang } from "./primitives";

/**
 * Kit de composición para páginas interiores en la dirección luminosa.
 * Sin dependencias nuevas: framer-motion, lucide y el Dialog de shadcn ya existen.
 * Todo el contenido es visible sin JavaScript y sin animación.
 */

/* ---------------------------------------------------------------- migas */

export type Crumb = { label: string; to?: string };

/**
 * Migas de pan con el mismo BreadcrumbList que emitía `BreadcrumbNav`,
 * para no perder datos estructurados al cambiar la composición.
 */
export const CrumbSchema = ({ items }: { items: Crumb[] }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.label,
          ...(item.to ? { item: `https://luisroc.com${item.to}` } : {}),
        })),
      })}
    </script>
  </Helmet>
);

export const Crumbs = ({ items }: { items: Crumb[] }) => (
  <nav aria-label="Breadcrumb">
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.78rem]">
      {items.map((item, i) => (
        <li key={`${item.label}-${i}`} className="flex items-center gap-2">
          {item.to ? (
            <Link
              to={item.to}
              className="text-[color:var(--v3-muted)] underline-offset-4 transition-colors hover:text-[color:var(--v3-blue)] hover:underline"
            >
              {item.label}
            </Link>
          ) : (
            <span className="text-[color:var(--v3-ink)]">{item.label}</span>
          )}
          {i < items.length - 1 ? (
            <ChevronRight className="h-3.5 w-3.5 text-[color:var(--v3-muted)]" aria-hidden />
          ) : null}
        </li>
      ))}
    </ol>
  </nav>
);

/* ------------------------------------------------- encabezado interior */

/**
 * Encabezado compacto de interior: migas, H1, definición concreta,
 * destinatario y acciones. No repite el hero arquitectónico de la Home.
 */
export const InteriorHead = ({
  crumbs,
  eyebrow,
  title,
  definition,
  audienceLabel,
  audience,
  actions,
  aside,
  facts,
}: {
  crumbs: Crumb[];
  eyebrow?: string;
  title: ReactNode;
  definition?: ReactNode;
  audienceLabel?: string;
  audience?: ReactNode;
  actions?: ReactNode;
  aside?: ReactNode;
  facts?: readonly { label: string; value: ReactNode }[];
}) => {
  const hasAside = Boolean(aside) || Boolean(facts?.length);
  return (
  <header className="border-b border-[color:var(--v3-line)] bg-[color:var(--v3-mineral)]">
    <div className="v3-shell pb-10 pt-8 lg:pb-14 lg:pt-12">
      <CrumbSchema items={crumbs} />
      <Crumbs items={crumbs} />
      <div className="mt-7 grid gap-10 lg:grid-cols-12 lg:gap-14">
        <div className={hasAside ? "min-w-0 lg:col-span-7" : "min-w-0 lg:col-span-9"}>
          {eyebrow ? <p className="v3-micro">{eyebrow}</p> : null}
          <h1
            className="v3-display mt-4"
            style={{ fontSize: "clamp(2.125rem, 3.6vw, 3.5rem)", lineHeight: 1.05 }}
          >
            {title}
          </h1>
          {definition ? (
            <p className="v3-body mt-6 text-[1.1rem]">{definition}</p>
          ) : null}
          {audience ? (
            <p className="v3-note mt-4">
              {audienceLabel ? (
                <strong className="font-semibold text-[color:var(--v3-ink)]">
                  {audienceLabel}{" "}
                </strong>
              ) : null}
              {audience}
            </p>
          ) : null}
          {actions ? <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3">{actions}</div> : null}
        </div>
        {hasAside ? (
          <div className="min-w-0 lg:col-span-5">
            {aside}
            {facts?.length ? (
              <dl className="border-t border-[color:var(--v3-line)]">
                {facts.map((f) => (
                  <div
                    key={f.label}
                    className="grid grid-cols-[minmax(0,9rem)_1fr] gap-4 border-b border-[color:var(--v3-line)] py-3"
                  >
                    <dt className="v3-micro pt-1">{f.label}</dt>
                    <dd className="text-[0.95rem] leading-relaxed text-[color:var(--v3-ink-soft)]">
                      {f.value}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  </header>
  );
};

/* --------------------------------------------- navegación en la página */

export type SectionRef = { id: string; label: string };

/** Sigue la sección visible sin animar nada más que el estado activo. */
export const useActiveSection = (ids: string[]) => {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);
  const key = ids.join("|");

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const list = key ? key.split("|") : [];
    const nodes = list
      .map((id) => document.getElementById(id))
      .filter((n): n is HTMLElement => Boolean(n));
    if (!nodes.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target.id) setActive(visible.target.id);
      },
      { rootMargin: "-88px 0px -62% 0px", threshold: 0 },
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [key]);

  return active;
};

/** Índice lateral compacto (desktop). Enlaces reales, sin hover obligatorio. */
export const SectionIndex = ({
  items,
  label,
  className = "",
}: {
  items: SectionRef[];
  label: string;
  className?: string;
}) => {
  const ids = useMemo(() => items.map((i) => i.id), [items]);
  const active = useActiveSection(ids);
  return (
    <nav aria-label={label} className={className}>
      <p className="v3-micro">{label}</p>
      <ol className="mt-4 space-y-0">
        {items.map((item, i) => {
          const on = active === item.id;
          return (
            <li key={item.id} className="border-t border-[color:var(--v3-line)] first:border-t-0">
              <a
                href={`#${item.id}`}
                aria-current={on ? "true" : undefined}
                className="flex items-baseline gap-3 py-2.5 text-[0.9rem] leading-snug transition-colors"
                style={{
                  color: on ? "var(--v3-blue)" : "var(--v3-ink-soft)",
                  transitionDuration: "var(--v3-t-control)",
                }}
              >
                <span className="v3-micro" style={{ color: on ? "var(--v3-blue)" : undefined }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{item.label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/** Tira compacta de secciones para móvil: se desplaza dentro de su contenedor. */
export const SectionStrip = ({ items, label }: { items: SectionRef[]; label: string }) => {
  const ids = useMemo(() => items.map((i) => i.id), [items]);
  const active = useActiveSection(ids);
  return (
    <nav
      aria-label={label}
      className="sticky top-[var(--v3-header-h)] z-30 max-w-full border-y border-[color:var(--v3-line)] bg-[color:var(--v3-mineral)]/95 backdrop-blur-md lg:hidden"
    >
      <ul className="flex max-w-full snap-x gap-5 overflow-x-auto py-2.5">
        {items.map((item) => {
          const on = active === item.id;
          return (
            <li key={item.id} className="snap-start whitespace-nowrap">
              <a
                href={`#${item.id}`}
                aria-current={on ? "true" : undefined}
                className="inline-flex min-h-[2.25rem] items-center border-b-2 text-[0.85rem] font-medium transition-colors"
                style={{
                  color: on ? "var(--v3-blue)" : "var(--v3-ink-soft)",
                  borderColor: on ? "var(--v3-blue)" : "transparent",
                  transitionDuration: "var(--v3-t-control)",
                }}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

/* ----------------------------------------------------- bloques de texto */

export const Section = ({
  id,
  index,
  label,
  title,
  intro,
  children,
  tone = "mineral",
  className = "",
}: {
  id?: string;
  index?: string;
  label?: string;
  title?: ReactNode;
  intro?: ReactNode;
  children?: ReactNode;
  tone?: "mineral" | "white" | "deep";
  className?: string;
}) => {
  const bg =
    tone === "white"
      ? "var(--v3-white)"
      : tone === "deep"
        ? "var(--v3-mineral-deep)"
        : "var(--v3-mineral)";
  return (
    <section
      id={id}
      className={`v3-section-tight scroll-mt-24 ${className}`}
      style={{ background: bg }}
    >
      <div className="v3-shell">
        {label || index ? (
          <p className="v3-micro">
            {index ? (
              <>
                <span style={{ color: "var(--v3-blue)" }}>{index}</span>
                <span className="mx-2" aria-hidden>
                  ·
                </span>
              </>
            ) : null}
            {label}
          </p>
        ) : null}
        {title ? (
          <h2
            className="v3-display mt-4"
            style={{ fontSize: "clamp(1.55rem, 2.5vw, 2.2rem)" }}
          >
            {title}
          </h2>
        ) : null}
        {intro ? <p className="v3-body mt-5">{intro}</p> : null}
        {children ? <div className={title || intro ? "mt-9" : ""}>{children}</div> : null}
      </div>
    </section>
  );
};

/** Columna de lectura de 60–70 caracteres. */
export const ReadingColumn = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={`v3-reading ${className}`} style={{ maxWidth: "68ch" }}>
    {children}
  </div>
);

/** Panel contextual compacto: alcance o navegación, nunca un anuncio repetido. */
export const ContextPanel = ({
  title,
  children,
  sticky = true,
  className = "",
}: {
  title?: string;
  children: ReactNode;
  sticky?: boolean;
  className?: string;
}) => (
  <aside
    className={`min-w-0 border-l-2 border-[color:var(--v3-blue)] bg-[color:var(--v3-white)] p-6 ${
      sticky ? "lg:sticky lg:top-[calc(var(--v3-header-h)+2rem)]" : ""
    } ${className}`}
  >
    {title ? <p className="v3-micro">{title}</p> : null}
    <div className={title ? "mt-4" : ""}>{children}</div>
  </aside>
);

/* --------------------------------------------------------- listas / datos */

export const DeliverableList = ({
  items,
  columns = 2,
}: {
  items: { title: string; detail?: string }[];
  columns?: 1 | 2 | 3;
}) => (
  <ul
    className={`grid gap-x-10 gap-y-0 ${
      columns === 3 ? "md:grid-cols-3" : columns === 2 ? "md:grid-cols-2" : ""
    }`}
  >
    {items.map((item, i) => (
      <li
        key={`${item.title}-${i}`}
        className="border-t border-[color:var(--v3-line)] py-4"
      >
        <p className="flex gap-3 text-[0.95rem] font-semibold text-[color:var(--v3-ink)]">
          <span className="v3-micro pt-1" style={{ color: "var(--v3-blue)" }}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <span>{item.title}</span>
        </p>
        {item.detail ? <p className="v3-note mt-1.5 pl-9">{item.detail}</p> : null}
      </li>
    ))}
  </ul>
);

/** Recorrido de proceso conectado, sin pinning ni altura artificial. */
const ProcessStep = ({
  children,
  delay,
}: {
  children: ReactNode;
  delay: number;
}) => {
  const ref = useRevealRef<HTMLLIElement>(delay);
  return (
    <li ref={ref} className="bg-[color:var(--v3-white)] p-6">
      {children}
    </li>
  );
};

export const ProcessTrack = ({
  steps,
}: {
  steps: { title: string; detail?: string; output?: string }[];
}) => {
  return (
    <ol className="relative grid gap-px bg-[color:var(--v3-line)] md:grid-cols-2 xl:grid-cols-4">
      {steps.map((step, i) => (
        <ProcessStep key={`${step.title}-${i}`} delay={Math.min(i * 0.06, 0.24)}>
          <p className="v3-micro" style={{ color: "var(--v3-blue)" }}>
            {String(i + 1).padStart(2, "0")}
          </p>
          <p className="mt-3 text-[1rem] font-semibold text-[color:var(--v3-ink)]">{step.title}</p>
          {step.detail ? <p className="v3-note mt-2">{step.detail}</p> : null}
          {step.output ? (
            <p className="mt-4 border-t border-[color:var(--v3-line)] pt-3 text-[0.82rem] text-[color:var(--v3-copper)]">
              {step.output}
            </p>
          ) : null}
        </ProcessStep>
      ))}
    </ol>
  );
};

/* ------------------------------------------------------------- evidencia */

/**
 * Figura con pie explicativo y ampliación accesible (foco y Escape
 * gestionados por el Dialog de shadcn). La evidencia nunca se deforma.
 */
export const EvidenceFigure = ({
  src,
  alt,
  caption,
  zoomLabel,
  zoomTitle,
  priority = false,
  className = "",
  width,
  height,
}: {
  src: string;
  alt: string;
  caption?: ReactNode;
  zoomLabel: string;
  zoomTitle?: string;
  priority?: boolean;
  className?: string;
  /** Dimensiones reales del archivo: reservan el espacio antes de cargar. */
  width?: number;
  height?: number;
}) => {
  const [open, setOpen] = useState(false);
  const ratio = width && height ? `${width} / ${height}` : undefined;
  return (
    <figure className={className}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            className="group relative block w-full cursor-zoom-in border border-[color:var(--v3-line)] bg-[color:var(--v3-white)] p-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--v3-blue)]"
          >
            <img
              src={src}
              alt={alt}
              width={width}
              height={height}
              loading={priority ? "eager" : "lazy"}
              decoding="async"
              className="w-full object-contain"
              style={ratio ? { aspectRatio: ratio } : undefined}
            />
            <span
              className="absolute right-4 top-4 inline-flex items-center gap-2 bg-[color:var(--v3-ink)]/85 px-3 py-1.5 text-[0.75rem] font-medium text-white transition-opacity"
              style={{ transitionDuration: "var(--v3-t-control)" }}
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden />
              {zoomLabel}
            </span>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-6xl border-[color:var(--v3-line)] bg-white p-4">
          <DialogTitle className="sr-only">{zoomTitle ?? alt}</DialogTitle>
          <img
            src={src}
            alt={alt}
            width={width}
            height={height}
            className="max-h-[80vh] w-full object-contain"
            style={ratio ? { aspectRatio: ratio } : undefined}
          />
        </DialogContent>
      </Dialog>
      {caption ? <figcaption className="v3-caption mt-3">{caption}</figcaption> : null}
    </figure>
  );
};

/* ------------------------------------------------------------ progreso */

/**
 * Barra de progreso de lectura discreta (2 px). Escribe directamente sobre el
 * nodo con `transform: scaleX` y origen izquierdo: sin estado de React por
 * píxel, sin animar `width` y sin provocar re-render en cada scroll.
 */
export const ReadingProgress = ({ targetId }: { targetId: string }) => {
  const bar = useRef<HTMLDivElement>(null);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const el = document.getElementById(targetId);
    const node = bar.current;
    if (!el || !node) return;

    const paint = () => {
      raf.current = null;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const done = total > 0 ? -rect.top / total : 0;
      node.style.transform = `scaleX(${Math.max(0, Math.min(1, done))})`;
    };
    const onScroll = () => {
      if (raf.current === null) raf.current = window.requestAnimationFrame(paint);
    };

    paint();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, [targetId]);

  return (
    <div
      className="sticky top-[var(--v3-header-h)] z-30 h-[2px] w-full bg-transparent"
      role="presentation"
      aria-hidden
    >
      <div
        ref={bar}
        className="h-full w-full origin-left bg-[color:var(--v3-blue)]"
        style={{ transform: "scaleX(0)" }}
      />
    </div>
  );
};

/* ------------------------------------------------- selector de evidencia */

export type EvidenceItem = {
  src: string;
  alt: string;
  caption?: ReactNode;
  /** Nombre corto para el selector manual. */
  name: string;
  width?: number;
  height?: number;
};

/**
 * Varias evidencias del mismo caso: selector manual con nombre y miniatura.
 * La imagen y su pie cambian juntos en un fundido corto, sin autoplay.
 * Sin JavaScript se ve la primera evidencia completa y los nombres del resto.
 */
export const EvidenceSwitcher = ({
  items,
  label,
  zoomLabel,
  className = "",
}: {
  items: EvidenceItem[];
  label: string;
  zoomLabel: string;
  className?: string;
}) => {
  const [i, setI] = useState(0);
  const current = items[Math.min(i, items.length - 1)];
  if (!items.length) return null;

  return (
    <div className={`grid gap-8 lg:grid-cols-12 lg:gap-12 ${className}`}>
      <div className="min-w-0 lg:col-span-4">
        <p className="v3-micro">{label}</p>
        <ul className="mt-4 list-none space-y-px pl-0">
          {items.map((item, index) => {
            const on = index === Math.min(i, items.length - 1);
            return (
              <li key={item.src}>
                {/* Enlace HTML real: sin JavaScript abre la evidencia a tamaño
                    completo; con JavaScript selecciona la imagen del visor. */}
                <a
                  href={item.src}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-current={on ? "true" : undefined}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    setI(index);
                  }}
                  className="flex min-h-[3rem] w-full items-start gap-3 border-l-2 bg-[color:var(--v3-white)] p-2.5 text-left no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--v3-blue)]"
                  style={{
                    borderLeftColor: on ? "var(--v3-blue)" : "var(--v3-line)",
                    transition:
                      "border-color var(--v3-t-control) var(--v3-ease), background var(--v3-t-control) var(--v3-ease)",
                  }}
                >
                  <img
                    src={item.src}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    decoding="async"
                    width={item.width}
                    height={item.height}
                    className="h-11 w-16 shrink-0 border border-[color:var(--v3-line)] object-cover"
                  />
                  <span className="min-w-0">
                    <span
                      className="block text-[0.88rem] font-medium leading-snug"
                      style={{ color: on ? "var(--v3-blue)" : "var(--v3-ink-soft)" }}
                    >
                      {item.name}
                    </span>
                    <span
                      className="mt-1 block text-[0.78rem] leading-snug"
                      style={{ color: "var(--v3-ink-soft)" }}
                    >
                      {item.caption ?? item.alt}
                    </span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="min-w-0 lg:col-span-8">
        <EvidenceFigure
          key={current.src}
          className="v3-swap"
          src={current.src}
          alt={current.alt}
          caption={current.caption}
          width={current.width}
          height={current.height}
          zoomLabel={zoomLabel}
        />
      </div>
    </div>
  );
};

/* -------------------------------------------------------------- acciones */

export const ActionLink = ({
  to,
  children,
  variant = "solid",
  external = false,
}: {
  to: string;
  children: ReactNode;
  variant?: "solid" | "ghost";
  external?: boolean;
}) => {
  const cls = variant === "solid" ? "v3-btn" : "v3-btn-ghost";
  if (external) {
    return (
      <a href={to} className={cls} rel="noopener">
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      {children}
    </Link>
  );
};

export const strings = {
  es: {
    home: "Inicio",
    services: "Servicios",
    cases: "Casos de éxito",
    library: "Biblioteca",
    blog: "Blog",
    onThisPage: "En esta página",
    contents: "Contenido",
    enlarge: "Ampliar",
    scope: "Alcance",
    deliverables: "Entregables",
    process: "Proceso",
    evidence: "Evidencia",
    faq: "Preguntas",
    contact: "Contacto",
  },
  en: {
    home: "Home",
    services: "Services",
    cases: "Case studies",
    library: "Library",
    blog: "Blog",
    onThisPage: "On this page",
    contents: "Contents",
    enlarge: "Enlarge",
    scope: "Scope",
    deliverables: "Deliverables",
    process: "Process",
    evidence: "Evidence",
    faq: "Questions",
    contact: "Contact",
  },
} as const satisfies Record<Lang, Record<string, string>>;
